import React from 'react';
import { motion } from 'framer-motion';
import { Link, Navigate } from 'react-router-dom';
import { ArrowLeft, Compass, Activity, ClipboardList, CircleDot, ChevronRight, Lock } from 'lucide-react';
import { useAuth } from '../context/AuthContext';

const tools = [
  {
    id: 'wellness-wheel',
    title: 'Wellness Wheel',
    subtitle: 'Daily Assessment',
    description: 'Rate each of the 9 Pillars of Wellness, see your balance wheel, and receive personalized guidance for your day.',
    icon: CircleDot,
    color: '#8BADA4',
    url: '/programs/terrain/wellness-wheel',
    available: true,
  },
  {
    id: 'metabolic-quiz',
    title: 'Metabolic Rhythm Quiz',
    subtitle: 'Understanding Your Fuel',
    description: 'How your nervous system prefers to be fueled — and how stress may be shaping your hunger, cravings, and energy.',
    icon: Activity,
    color: '#C88D74',
    url: '/quiz/metabolic-rhythm',
    available: true,
  },
  {
    id: 'vitality-tool',
    title: 'The Vitality Potential Tool',
    subtitle: 'Comprehensive Assessment',
    description: 'A comprehensive look at the factors that may be depleting your energy — and where your natural resilience lives.',
    icon: ClipboardList,
    color: '#9B8EC4',
    url: '/quiz/lifestyle-assessment',
    available: true,
  },
  {
    id: 'monthly-entries',
    title: 'Monthly Wellness Entries',
    subtitle: 'Coming Soon',
    description: 'Guided monthly check-ins to track your journey, reflect on progress, and set intentions for the month ahead.',
    icon: Compass,
    color: '#6BA3BE',
    url: '#',
    available: false,
  },
];

const SlowWellnessPage = () => {
  const { user, hasAccess, loading } = useAuth();

  if (loading) {
    return (
      <div className="bg-cream min-h-screen pt-24 flex items-center justify-center">
        <p className="text-stone-400 font-body">Loading...</p>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/programs" replace />;
  }

  const hasProgramAccess = hasAccess('terrain');

  return (
    <div className="bg-cream min-h-screen pt-24 pb-16" data-testid="slow-wellness-page">
      <div className="max-w-4xl mx-auto px-6">
        {/* Back link */}
        <Link
          to="/programs"
          className="inline-flex items-center gap-2 text-forest/70 hover:text-forest transition-colors mb-6"
          data-testid="back-to-programs"
        >
          <ArrowLeft size={18} />
          <span className="font-body text-sm">Back to Programs</span>
        </Link>

        {/* Hero */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="mb-10"
        >
          <p className="text-terracotta font-body text-sm tracking-widest uppercase mb-3">Foundation Program</p>
          <h1 className="font-heading text-4xl sm:text-5xl text-forest mb-4" data-testid="slow-wellness-title">
            SLOW WELLNESS
          </h1>
          <p className="text-stone-600 font-body text-base sm:text-lg leading-relaxed max-w-2xl">
            Tapping into your Terrain. A year-long journey to rebuild your health from the ground up — addressing sleep, 
            nutrition, movement, stress, and the deeper aspects of wellbeing that most programs miss.
          </p>
        </motion.div>

        {/* Access Gate */}
        {!hasProgramAccess && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="bg-terracotta/10 border border-terracotta/20 rounded-xl p-6 mb-8 text-center"
            data-testid="access-required-banner"
          >
            <Lock size={24} className="text-terracotta mx-auto mb-3" />
            <p className="font-body text-forest font-medium mb-1">Program Access Required</p>
            <p className="font-body text-stone-500 text-sm mb-4">
              Contact Jill to unlock the SLOW WELLNESS program and access all tools below.
            </p>
            <a
              href="mailto:heyjbennett@yahoo.ca"
              className="inline-block bg-terracotta text-white px-6 py-2 rounded-full font-body text-sm hover:bg-terracotta-dark transition-colors"
            >
              Contact to Register
            </a>
          </motion.div>
        )}

        {/* Quote */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.15 }}
          className="bg-white rounded-2xl p-8 border border-stone-100 mb-10"
        >
          <p className="font-accent text-xl sm:text-2xl text-forest/80 italic leading-relaxed text-center">
            "When the most strained areas are supported, the entire system begins to regulate."
          </p>
          <p className="text-center text-stone-400 font-body text-sm mt-3">— The 9 Pillars of Wellness</p>
        </motion.div>

        {/* Tools Grid */}
        <div className="mb-4">
          <h2 className="font-heading text-2xl text-forest mb-1">Your Tools</h2>
          <p className="font-body text-stone-500 text-sm">Assessments, daily check-ins, and self-discovery tools for your journey.</p>
        </div>

        <div className="grid gap-4 sm:grid-cols-2 mb-12">
          {tools.map((tool, idx) => {
            const Icon = tool.icon;
            const isLocked = !hasProgramAccess || !tool.available;
            const Wrapper = isLocked ? 'div' : Link;
            const wrapperProps = isLocked ? {} : { to: tool.url };

            return (
              <motion.div
                key={tool.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: 0.1 + idx * 0.08 }}
              >
                <Wrapper
                  {...wrapperProps}
                  className={`block bg-white rounded-xl border border-stone-100 p-5 h-full transition-all ${
                    isLocked ? 'opacity-60' : 'hover:shadow-md hover:border-stone-200 cursor-pointer'
                  }`}
                  data-testid={`tool-card-${tool.id}`}
                >
                  <div className="flex items-start gap-4">
                    <div
                      className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0"
                      style={{ backgroundColor: tool.color + '20' }}
                    >
                      <Icon size={20} style={{ color: tool.color }} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <h3 className="font-body text-forest font-medium text-sm">{tool.title}</h3>
                        {!tool.available && (
                          <span className="text-xs bg-stone-100 text-stone-500 px-2 py-0.5 rounded-full font-body">
                            Coming Soon
                          </span>
                        )}
                      </div>
                      <p className="text-stone-400 font-body text-xs mb-2">{tool.subtitle}</p>
                      <p className="text-stone-600 font-body text-sm leading-relaxed">{tool.description}</p>
                    </div>
                    {!isLocked && (
                      <ChevronRight size={18} className="text-stone-300 flex-shrink-0 mt-1" />
                    )}
                  </div>
                </Wrapper>
              </motion.div>
            );
          })}
        </div>

        {/* The Journey Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.4 }}
          className="bg-forest text-cream rounded-2xl p-8 mb-8"
        >
          <h3 className="font-heading text-2xl mb-4">How This Becomes a One-Year Journey</h3>
          <p className="font-body text-cream/80 leading-relaxed mb-4">
            This framework isn't meant to be rushed — it is designed for your highest goals of longevity. Over a year, 
            each pillar can be explored, revisited, and re-integrated as life evolves.
          </p>
          <p className="font-body text-cream/80 leading-relaxed mb-4">
            The work becomes less about discipline and more about attunement. Less about fixing and more about listening. 
            Less about pushing and more about stability and trust.
          </p>
          <p className="font-accent text-lg text-terracotta italic">
            "Are you willing to give it a year?"
          </p>
        </motion.div>
      </div>
    </div>
  );
};

export default SlowWellnessPage;
